import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const InvestorGovernanceHighlightsPage = () => {
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingHighlight, setEditingHighlight] = useState(null);
    const [formData, setFormData] = useState({
        highlight_text: '',
        display_order: 0,
        is_active: true
    });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [highlightIdToDelete, setHighlightIdToDelete] = useState(null);

    useEffect(() => {
        fetchHighlights();
    }, []);

    const fetchHighlights = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/investor-trust/highlights');
            if (response.data.success) {
                setHighlights(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching highlights:', error);
            toast.error('Failed to load highlights');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingHighlight(null);
        setFormData({
            highlight_text: '',
            display_order: highlights.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (highlight) => {
        setEditingHighlight(highlight);
        setFormData({
            highlight_text: highlight.highlight_text,
            display_order: highlight.display_order,
            is_active: highlight.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setHighlightIdToDelete(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!highlightIdToDelete) return;

        try {
            await axios.delete(`/api/admin/investor-trust/highlights/${highlightIdToDelete}`);
            toast.success('Highlight deleted successfully');
            fetchHighlights();
        } catch (error) {
            console.error('Error deleting highlight:', error);
            toast.error('Failed to delete highlight');
        } finally {
            setShowDeleteConfirm(false);
            setHighlightIdToDelete(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingHighlight) {
                await axios.put(`/api/admin/investor-trust/highlights/${editingHighlight.id}`, formData);
                toast.success('Highlight updated successfully');
            } else {
                await axios.post('/api/admin/investor-trust/highlights', formData);
                toast.success('Highlight created successfully');
            }
            setIsModalOpen(false);
            fetchHighlights();
        } catch (error) {
            console.error('Error saving highlight:', error);
            toast.error('Failed to save highlight');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Governance Highlights</h1>
                    <p className="text-slate-500 mt-1">Manage the highlights for Trust & Integrity section</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                    <PlusIcon size={20} />
                    Add New Highlight
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : highlights.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <p className="text-slate-500">No highlights yet. Add your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {highlights.map((highlight) => (
                        <div key={highlight.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <p className="text-slate-700 font-medium">{highlight.highlight_text}</p>
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => handleEdit(highlight)}
                                        className="p-2 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                    >
                                        <PencilIcon size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(highlight.id)}
                                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <TrashIcon size={18} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                                <span>Order: {highlight.display_order}</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${highlight.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {highlight.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold text-slate-800 mb-4">
                            {editingHighlight ? 'Edit Highlight' : 'Add Highlight'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Highlight Text</label>
                                <input
                                    type="text"
                                    value={formData.highlight_text}
                                    onChange={(e) => setFormData({ ...formData, highlight_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Transparent leadership accountability"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                <input
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    min="0"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                                    Active
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors"
                                >
                                    {editingHighlight ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Governance Highlight"
                message="Are you sure you want to delete this highlight? This action cannot be undone."
            />
        </div>
    );
};

export default InvestorGovernanceHighlightsPage;
